// Wave 3.51 Phase B — Reusable lock banner for packet / jewellery detail
// pages. Fetches any open Edit Requests whose lock scope intersects the
// provided ids and renders a single amber banner with deep link to the
// Edit Requests page. Used at the TOP of detail pages; per-row amber
// highlighting is handled separately in the ledger row markup.
//
// Returns `null` when there is no pending lock — the host page renders
// nothing extra. Otherwise renders an inline <div> banner with the
// standard Wave 3.51 user-facing copy:
//   "Property locked as there is a pending Edit Request on the Property"

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PencilLine } from 'lucide-react';
import { api } from '@/lib/api';

/**
 * @param {object} props
 * @param {string} [props.packetId]
 * @param {string} [props.orderId]
 * @param {string} [props.goldsmithId]
 * @param {string} [props.issueId]
 * @param {string} [props.returnId]
 * @param {number} [props.refreshKey]  bump to force re-fetch
 * @param {function} [props.onPendingChange]  notified with the list
 */
export function PacketEditLockBanner({
  packetId, orderId, goldsmithId, issueId, returnId,
  refreshKey = 0, onPendingChange,
}) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!packetId && !orderId && !goldsmithId && !issueId && !returnId) {
      setPending([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (packetId) params.packet_id = packetId;
    if (orderId) params.order_id = orderId;
    if (goldsmithId) params.goldsmith_id = goldsmithId;
    if (issueId) params.issue_id = issueId;
    if (returnId) params.return_id = returnId;
    api
      .get('/edit-requests/pending', { params })
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setPending(list);
        if (onPendingChange) onPendingChange(list);
      })
      .catch(() => {
        if (cancelled) return;
        setPending([]);
        if (onPendingChange) onPendingChange([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [packetId, orderId, goldsmithId, issueId, returnId, refreshKey, onPendingChange]);

  if (loading || pending.length === 0) return null;
  return (
    <div
      className="mb-3 text-xs bg-amber-50 text-amber-800 border border-amber-200 px-3 py-2 rounded flex items-center gap-2"
      data-testid="packet-edit-lock-banner"
    >
      <PencilLine size={14} />
      <span>
        <strong>Property locked</strong> — there {pending.length === 1 ? 'is a' : `are ${pending.length}`} pending
        {' '}Edit Request{pending.length === 1 ? '' : 's'} on this property. Further
        transactions are blocked until the Super Admin acts on
        {pending.length === 1 ? ' it' : ' them'}.
      </span>
      <Link
        to="/edit-requests"
        className="ml-auto text-amber-900 underline hover:no-underline"
        data-testid="packet-edit-lock-banner-link"
      >
        View Edit Requests →
      </Link>
    </div>
  );
}

export default PacketEditLockBanner;
